import { devLog, devError } from '../utils/devLogger';
import { isRealUser } from '../utils/userFilters';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Card, Row, Col, Table, Badge, Button, Form,
  Spinner, Alert, ProgressBar, InputGroup
} from 'react-bootstrap';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, where, writeBatch, doc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import { SALES_CATEGORIES } from '../constants/salesCategories';

/**
 * Facilitator Sales Dashboard
 * Comprehensive view of all Take A Lot sales data with analytics
 */
const FacilitatorSalesDashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const functions = getFunctions();

  // State management
  const [salesData, setSalesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [importError, setImportError] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [importing, setImporting] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importHeaders, setImportHeaders] = useState([]);
  const [importSalesColumns, setImportSalesColumns] = useState([]);
  const [selectedImportSalesColumn, setSelectedImportSalesColumn] = useState('');
  const [replaceExistingImportMonth, setReplaceExistingImportMonth] = useState(true);
  const [importSummary, setImportSummary] = useState(null);
  const [deletingImportBatch, setDeletingImportBatch] = useState('');

  // Filter state
  const [filters, setFilters] = useState({
    searchTerm: '',
    category: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: ''
  });
  const [scopedUserIds, setScopedUserIds] = useState(null);

  // Analytics state
  const [analytics, setAnalytics] = useState({
    totalSales: 0,
    totalTransactions: 0,
    totalItems: 0,
    totalLearners: 0,
    averageTransactionValue: 0,
    topPerformer: null,
    categoryBreakdown: {},
    recentGrowth: 0
  });

  // Access control
  useEffect(() => {
    if (userProfile && !['facilitator', 'admin', 'qa'].includes(userProfile.role)) {
      navigate('/dashboard');
    }
  }, [userProfile, navigate]);

  useEffect(() => {
    const loadScope = async () => {
      try {
        if (!userProfile) return;
        const role = userProfile.role;
        if (role === 'admin' || role === 'qa') {
          setScopedUserIds(null);
          return;
        }
        if (role === 'facilitator' || role === 'external_facilitator') {
          let cohortIds = Array.isArray(userProfile.assignedCohorts) ? userProfile.assignedCohorts : [];
          if (!cohortIds.length && Array.isArray(userProfile.assignedPrograms) && userProfile.assignedPrograms.length) {
            const cohortSnapshot = await getDocs(collection(db, 'cohorts'));
            cohortIds = cohortSnapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .filter(cohort => userProfile.assignedPrograms.includes(cohort.programId))
              .map(cohort => cohort.id);
          }

          if (!cohortIds.length) {
            setScopedUserIds(new Set());
            return;
          }

          const userIds = new Set();
          for (let i = 0; i < cohortIds.length; i += 10) {
            const chunk = cohortIds.slice(i, i + 10);
            const usersSnapshot = await getDocs(
              query(collection(db, 'users'), where('role', '==', 'learner'), where('cohortId', 'in', chunk))
            );
            usersSnapshot.docs.forEach(doc => userIds.add(doc.id));
          }
          setScopedUserIds(userIds);
          return;
        }

        setScopedUserIds(null);
      } catch (error) {
        devError('Error loading sales scope:', error);
        setScopedUserIds(null);
      }
    };

    loadScope();
  }, [userProfile]);

  // Load data on mount
  useEffect(() => {
    loadSalesData();
  }, [scopedUserIds]);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [salesData, filters]);

  // Recalculate analytics when filtered data changes
  useEffect(() => {
    calculateAnalytics();
  }, [filteredData]);

  /**
   * Load all sales data from Firestore
   */
  const loadSalesData = async () => {
    try {
      setLoading(true);
      setError('');

      const salesQuery = query(
        collection(db, 'sales'),
        orderBy('submittedAt', 'desc')
      );

      const snapshot = await getDocs(salesQuery);
      const sales = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate?.() || null,
        importedAt: doc.data().importedAt?.toDate?.() || null
      })).filter((sale) => {
        if (scopedUserIds instanceof Set) {
          return scopedUserIds.has(sale.userId);
        }
        return true;
      });

      setSalesData(sales);
    } catch (err) {
      devError('Error loading sales data:', err);
      setError('Failed to load sales data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const normalizeHeader = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

  const parseCSVRow = (row) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    if (!row || row.trim() === '') return [];

    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim().replace(/^"|"$/g, ''));
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim().replace(/^"|"$/g, ''));
    return result;
  };

  const parseCSVText = (text) => {
    const lines = String(text || '').split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length === 0) return { headers: [], rows: [] };
    const headers = parseCSVRow(lines[0]);
    const rows = [];
    for (let i = 1; i < lines.length; i += 1) {
      const values = parseCSVRow(lines[i]);
      if (!values.length) continue;
      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] ?? '';
      });
      rows.push(row);
    }
    return { headers, rows };
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportError('');
    setImportStatus('');
    setImportSummary(null);
    setImportSalesColumns([]);
    setSelectedImportSalesColumn('');

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result || '';
      const parsed = parseCSVText(text);
      const detectedSalesHeaders = getSalesHeaders(parsed.headers);
      setImportHeaders(parsed.headers || []);
      setImportRows(parsed.rows || []);
      setImportSalesColumns(detectedSalesHeaders);
      setSelectedImportSalesColumn(detectedSalesHeaders.length === 1 ? detectedSalesHeaders[0] : '');
      if (!parsed.rows?.length) {
        setImportError('No data rows found in CSV.');
      } else if (!detectedSalesHeaders.length) {
        setImportError('No "Sales Data - <Month>" columns were found in the CSV.');
      } else {
        setImportStatus(`Loaded ${parsed.rows.length} rows.`);
      }
    };
    reader.onerror = () => setImportError('Failed to read CSV file.');
    reader.readAsText(file);
  };

  const extractColumnValue = (row, headers, possibleNames) => {
    const headerMatch = headers.find((header) => {
      const normalized = normalizeHeader(header);
      return possibleNames.some((name) => normalized.includes(name));
    });
    return headerMatch ? row[headerMatch] : '';
  };

  const parseAmount = (value) => {
    if (value == null) return 0;
    const cleaned = String(value).replace(/[^0-9.-]+/g, '');
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getSalesHeaders = (headers) => {
    return (headers || []).filter((header) => normalizeHeader(header).includes('sales data'));
  };

  const getImportMonthKey = (header) => {
    const normalized = normalizeHeader(header);
    return normalized
      .replace('sales data', '')
      .replace(/^-+/, '')
      .trim()
      .replace(/\s+/g, '_');
  };

  const formatImportBatchLabel = (dateValue) => {
    if (!dateValue) return 'Unknown import time';
    return new Date(dateValue).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const importedSalesBatches = useMemo(() => {
    const batchMap = new Map();

    salesData
      .filter((sale) => sale.source === 'import')
      .forEach((sale) => {
        const importedAtValue = sale.importedAt || sale.submittedAt || null;
        const batchKey = importedAtValue ? new Date(importedAtValue).toISOString() : `unknown-${sale.id}`;
        if (!batchMap.has(batchKey)) {
          batchMap.set(batchKey, {
            batchKey,
            importedAt: importedAtValue,
            importMonth: sale.importMonth || 'legacy',
            importSourceColumn: sale.importSourceColumn || 'Legacy import',
            recordCount: 0,
            totalAmount: 0
          });
        }

        const batch = batchMap.get(batchKey);
        batch.recordCount += 1;
        batch.totalAmount += Number(sale.totalAmount || 0);
      });

    return Array.from(batchMap.values()).sort((a, b) => {
      const aTime = a.importedAt ? new Date(a.importedAt).getTime() : 0;
      const bTime = b.importedAt ? new Date(b.importedAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [salesData]);

  const handleDeleteImportedBatch = async (batchKey) => {
    const batchMeta = importedSalesBatches.find((batch) => batch.batchKey === batchKey);
    if (!batchMeta) {
      setImportError('Unable to find the selected imported batch.');
      return;
    }

    const confirmed = window.confirm(
      `Delete imported sales batch from ${formatImportBatchLabel(batchMeta.importedAt)} (${batchMeta.recordCount} records)?`
    );
    if (!confirmed) return;

    try {
      setDeletingImportBatch(batchKey);
      setImportError('');
      setImportStatus('');
      setImportSummary(null);

      const deleteImportedSalesBatchCallable = httpsCallable(functions, 'deleteImportedSalesBatch');
      const result = await deleteImportedSalesBatchCallable({ batchKey });
      const deletedCount = Number(result?.data?.deletedCount || 0);

      setImportStatus(`Deleted ${deletedCount} imported sales records from ${formatImportBatchLabel(batchMeta.importedAt)}.`);
      await loadSalesData();
    } catch (err) {
      devError('Error deleting imported sales batch:', err);
      setImportError('Failed to delete imported sales batch. Please try again.');
    } finally {
      setDeletingImportBatch('');
    }
  };

  const handleImportSales = async () => {
    try {
      setImportError('');
      setImportStatus('');
      setImportSummary(null);

      if (!importRows.length) {
        setImportError('Please upload a CSV file first.');
        return;
      }

      const headers = importHeaders || [];
      const emailHeaderKeys = ['email address', 'email'];
      const idHeaderKeys = ['identity number', 'id number', 'id no'];
      const salesHeader = selectedImportSalesColumn;
      const importMonthKey = getImportMonthKey(salesHeader);
      if (!salesHeader) {
        setImportError('Please select which "Sales Data - <Month>" column to import.');
        return;
      }

      setImporting(true);

      const learnersSnapshot = await getDocs(query(collection(db, 'users'), where('role', '==', 'learner')));
      const learners = learnersSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })).filter(isRealUser);
      const learnersByEmail = new Map();
      const learnersByIdNumber = new Map();
      learners.forEach((learner) => {
        const email = String(learner.email || '').toLowerCase().trim();
        const idNumber = String(learner.idNumber || learner.identityNumber || '').trim();
        if (email) learnersByEmail.set(email, learner);
        if (idNumber) learnersByIdNumber.set(idNumber, learner);
      });

      let imported = 0;
      let skippedNoSales = 0;
      let skippedNoLearner = 0;
      let replacedExisting = 0;
      const unmatchedRows = [];

      const batchSize = 400;
      let batch = writeBatch(db);
      let batchCount = 0;

      if (replaceExistingImportMonth) {
        const existingImportSnapshot = await getDocs(collection(db, 'sales'));
        const docsToDelete = existingImportSnapshot.docs.filter((docSnap) => {
          const data = docSnap.data() || {};
          if (data.source !== 'import') return false;
          if (data.programCode && data.programCode !== 'TAKE_A_LOT') return false;

          const recordMonth = String(data.importMonth || '').trim();
          if (recordMonth && recordMonth === importMonthKey) {
            return true;
          }

          // Legacy January imports had no importMonth stored, so allow cleanup when January is re-imported.
          if (!recordMonth && importMonthKey === 'january') {
            return true;
          }

          return false;
        });

        if (docsToDelete.length > 0) {
          let deleteBatch = writeBatch(db);
          let deleteCount = 0;

          for (const docSnap of docsToDelete) {
            deleteBatch.delete(docSnap.ref);
            deleteCount += 1;
            replacedExisting += 1;

            if (deleteCount >= batchSize) {
              await deleteBatch.commit();
              deleteBatch = writeBatch(db);
              deleteCount = 0;
            }
          }

          if (deleteCount > 0) {
            await deleteBatch.commit();
          }
        }
      }

      for (const row of importRows) {
        const email = String(extractColumnValue(row, headers, emailHeaderKeys) || '').toLowerCase().trim();
        const idNumber = String(extractColumnValue(row, headers, idHeaderKeys) || '').trim();
        const salesAmount = parseAmount(row[salesHeader]);

        if (!salesAmount || salesAmount <= 0) {
          skippedNoSales += 1;
          continue;
        }

        const learner = (email && learnersByEmail.get(email)) || (idNumber && learnersByIdNumber.get(idNumber));
        if (!learner) {
          skippedNoLearner += 1;
          unmatchedRows.push({ email, idNumber, salesAmount });
          continue;
        }

        const saleRef = doc(collection(db, 'sales'));
        batch.set(saleRef, {
          userId: learner.id,
          userName: learner.displayName || learner.fullName || learner.name || `${row.Name || ''} ${row.Surname || ''}`.trim() || 'Unknown',
          userEmail: learner.email || email || '',
          programName: 'Take A Lot',
          programCode: 'TAKE_A_LOT',
          salesEntries: [{
            category: 'Imported',
            itemName: 'Imported to system',
            amount: salesAmount
          }],
          totalAmount: salesAmount,
          entryCount: 1,
          notes: 'Imported from CSV',
          source: 'import',
          importMonth: importMonthKey,
          importSourceColumn: salesHeader,
          importedBy: currentUser?.uid || null,
          importedAt: serverTimestamp(),
          submittedAt: serverTimestamp(),
          createdAt: serverTimestamp()
        });

        imported += 1;
        batchCount += 1;

        if (batchCount >= batchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      setImportSummary({
        imported,
        replacedExisting,
        skippedNoSales,
        skippedNoLearner,
        unmatchedRows
      });
      setImportStatus(`Imported ${imported} records${replaceExistingImportMonth ? ` after replacing ${replacedExisting} existing ${salesHeader} imports` : ''}.`);
      await loadSalesData();
    } catch (err) {
      devError('Error importing sales:', err);
      setImportError('Failed to import sales data. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  /**
   * Apply filters to sales data
   */
  const applyFilters = () => {
    let filtered = [...salesData];

    // Search term (user name or email)
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(sale =>
        sale.userName?.toLowerCase().includes(term) ||
        sale.userEmail?.toLowerCase().includes(term)
      );
    }

    // Category filter
    if (filters.category) {
      filtered = filtered.filter(sale =>
        sale.salesEntries?.some(entry => entry.category === filters.category)
      );
    }

    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(sale => sale.submittedAt >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(sale => sale.submittedAt <= toDate);
    }

    // Amount range filter
    if (filters.minAmount) {
      filtered = filtered.filter(sale => sale.totalAmount >= parseFloat(filters.minAmount));
    }
    if (filters.maxAmount) {
      filtered = filtered.filter(sale => sale.totalAmount <= parseFloat(filters.maxAmount));
    }

    setFilteredData(filtered);
  };

  /**
   * Calculate analytics from filtered data
   */
  const calculateAnalytics = () => {
    if (filteredData.length === 0) {
      setAnalytics({
        totalSales: 0,
        totalTransactions: 0,
        totalItems: 0,
        totalLearners: 0,
        averageTransactionValue: 0,
        topPerformer: null,
        categoryBreakdown: {},
        recentGrowth: 0
      });
      return;
    }

    // Aggregate by user
    const userAggregates = {};
    const categoryTotals = {};
    let totalSales = 0;
    let totalItems = 0;

    filteredData.forEach(sale => {
      const userId = sale.userId;
      totalSales += sale.totalAmount || 0;
      totalItems += sale.entryCount || 0;

      // User aggregation
      if (!userAggregates[userId]) {
        userAggregates[userId] = {
          userId,
          userName: sale.userName,
          userEmail: sale.userEmail,
          totalSales: 0,
          transactionCount: 0
        };
      }
      userAggregates[userId].totalSales += sale.totalAmount || 0;
      userAggregates[userId].transactionCount += 1;

      // Category aggregation
      sale.salesEntries?.forEach(entry => {
        if (!categoryTotals[entry.category]) {
          categoryTotals[entry.category] = 0;
        }
        categoryTotals[entry.category] += entry.amount || 0;
      });
    });

    // Find top performer
    const topPerformer = Object.values(userAggregates).reduce((top, current) =>
      current.totalSales > (top?.totalSales || 0) ? current : top
    , null);

    // Calculate recent growth (last 7 days vs previous 7 days)
    const now = new Date();
    const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const previous7Days = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

    const recentSales = filteredData
      .filter(sale => sale.submittedAt >= last7Days)
      .reduce((sum, sale) => sum + (sale.totalAmount || 0), 0);

    const previousSales = filteredData
      .filter(sale => sale.submittedAt >= previous7Days && sale.submittedAt < last7Days)
      .reduce((sum, sale) => sum + (sale.totalAmount || 0), 0);

    const recentGrowth = previousSales > 0
      ? ((recentSales - previousSales) / previousSales) * 100
      : 0;

    setAnalytics({
      totalSales,
      totalTransactions: filteredData.length,
      totalItems,
      totalLearners: Object.keys(userAggregates).length,
      averageTransactionValue: totalSales / filteredData.length,
      topPerformer,
      categoryBreakdown: categoryTotals,
      recentGrowth
    });
  };

  /**
   * Export data to CSV
   */
  const exportToCSV = () => {
    if (filteredData.length === 0) {
      alert('No data to export');
      return;
    }

    // Flatten sales entries for CSV
    const rows = [];
    filteredData.forEach(sale => {
      sale.salesEntries?.forEach(entry => {
        rows.push({
          'Transaction Date': sale.submittedAt?.toLocaleDateString('en-ZA'),
          'Learner Name': sale.userName,
          'Learner Email': sale.userEmail,
          'Category': entry.category,
          'Item Name': entry.itemName,
          'Amount': entry.amount,
          'Transaction Total': sale.totalAmount,
          'Notes': sale.notes || ''
        });
      });
    });

    // Convert to CSV
    const headers = Object.keys(rows[0]);
    const csvContent = [
      headers.join(','),
      ...rows.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `take-a-lot-sales-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      category: '',
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: ''
    });
  };

  /**
   * Get top 10 learners by total sales
   */
  const getTopLearners = () => {
    const userAggregates = {};

    filteredData.forEach(sale => {
      const userId = sale.userId;
      if (!userAggregates[userId]) {
        userAggregates[userId] = {
          userId,
          userName: sale.userName,
          userEmail: sale.userEmail,
          totalSales: 0,
          transactionCount: 0
        };
      }
      userAggregates[userId].totalSales += sale.totalAmount || 0;
      userAggregates[userId].transactionCount += 1;
    });

    return Object.values(userAggregates)
      .sort((a, b) => b.totalSales - a.totalSales)
      .slice(0, 10);
  };

  if (loading) {
    return (
      <>
        <FacilitatorNavbar currentPage="sales" />
        <Container className="mt-4">
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading sales dashboard...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <FacilitatorNavbar currentPage="sales" />
      <Container className="mt-4 mb-5">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2>📊 Take A Lot Sales Dashboard</h2>
            <p className="text-muted mb-0">
              Comprehensive analytics and tracking for sales performance
            </p>
          </div>
          <div>
            <Button variant="outline-primary" onClick={loadSalesData} className="me-2">
              ↻ Refresh
            </Button>
            <Button variant="success" onClick={exportToCSV} disabled={filteredData.length === 0}>
              📥 Export CSV
            </Button>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {/* Analytics Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Total Sales</div>
                <h3 className="text-success mb-0">
                  R {analytics.totalSales.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </h3>
                {analytics.recentGrowth !== 0 && (
                  <small className={analytics.recentGrowth > 0 ? 'text-success' : 'text-danger'}>
                    {analytics.recentGrowth > 0 ? '↑' : '↓'} {Math.abs(analytics.recentGrowth).toFixed(1)}% (7 days)
                  </small>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Transactions</div>
                <h3 className="text-primary mb-0">{analytics.totalTransactions}</h3>
                <small className="text-muted">{analytics.totalItems} items sold</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Active Learners</div>
                <h3 className="text-info mb-0">{analytics.totalLearners}</h3>
                <small className="text-muted">Recording sales</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body>
                <div className="text-muted small mb-1">Avg Transaction</div>
                <h3 className="text-warning mb-0">
                  R {analytics.averageTransactionValue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </h3>
                <small className="text-muted">Per transaction</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Top Performer Card */}
        {analytics.topPerformer && (
          <Alert variant="success" className="mb-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>🏆 Top Performer:</strong> {analytics.topPerformer.userName}
              </div>
              <div>
                <Badge bg="success" className="me-2">{analytics.topPerformer.transactionCount} transactions</Badge>
                <strong className="text-success">
                  R {analytics.topPerformer.totalSales.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
              </div>
            </div>
          </Alert>
        )}

        {/* CSV Import */}
        <Card className="shadow-sm mb-4">
          <Card.Header className="bg-white">
            <h6 className="mb-0">Bulk Sales Import (CSV)</h6>
          </Card.Header>
          <Card.Body>
            <p className="text-muted">
              Upload a CSV with learner details and a <strong>Sales Data - {'<Month>'}</strong> column.
              Imported sales will show a single itemised entry: “Imported to system”.
            </p>
            {importError && (
              <Alert variant="danger" onClose={() => setImportError('')} dismissible>
                {importError}
              </Alert>
            )}
            {importStatus && (
              <Alert variant="info" onClose={() => setImportStatus('')} dismissible>
                {importStatus}
              </Alert>
            )}
            {importSummary && (
              <Alert variant="success" onClose={() => setImportSummary(null)} dismissible>
                Imported {importSummary.imported} records.
                Replaced existing for selected month: {importSummary.replacedExisting}.
                Skipped (no sales): {importSummary.skippedNoSales}. Skipped (no learner match): {importSummary.skippedNoLearner}.
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Upload CSV</Form.Label>
              <Form.Control type="file" accept=".csv,text/csv" onChange={handleImportFile} />
              <Form.Text className="text-muted">
                Required columns: Email Address or Identity Number, Sales Data - {'<Month>'}.
              </Form.Text>
            </Form.Group>
            {importSalesColumns.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Choose Sales Month Column</Form.Label>
                <Form.Select
                  value={selectedImportSalesColumn}
                  onChange={(e) => setSelectedImportSalesColumn(e.target.value)}
                >
                  <option value="">Select a sales column</option>
                  {importSalesColumns.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  If the file contains January, February, and March columns, choose the exact month you want to import.
                </Form.Text>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="replace-existing-import-month"
                label="Replace previously imported records for the selected sales month"
                checked={replaceExistingImportMonth}
                onChange={(e) => setReplaceExistingImportMonth(e.target.checked)}
              />
              <Form.Text className="text-muted">
                Recommended. This lets you re-upload a month cleanly instead of stacking duplicate imported sales.
              </Form.Text>
            </Form.Group>
            {importRows.length > 0 && (
              <div className="mb-3 text-muted">
                Loaded {importRows.length} rows.
              </div>
            )}
            <Button
              variant="primary"
              onClick={handleImportSales}
              disabled={importing || importRows.length === 0 || !selectedImportSalesColumn}
            >
              {importing ? 'Importing...' : 'Import Sales'}
            </Button>

            {importedSalesBatches.length > 0 && (
              <>
                <hr />
                <h6 className="mb-3">Imported Batches</h6>
                <Table responsive size="sm" bordered hover>
                  <thead>
                    <tr>
                      <th>Imported At</th>
                      <th>Month</th>
                      <th>Source Column</th>
                      <th>Records</th>
                      <th>Total</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importedSalesBatches.map((batch) => (
                      <tr key={batch.batchKey}>
                        <td>{formatImportBatchLabel(batch.importedAt)}</td>
                        <td>{String(batch.importMonth || 'legacy').replace(/_/g, ' ')}</td>
                        <td>{batch.importSourceColumn}</td>
                        <td>{batch.recordCount}</td>
                        <td>R {batch.totalAmount.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={deletingImportBatch === batch.batchKey}
                            onClick={() => handleDeleteImportedBatch(batch.batchKey)}
                          >
                            {deletingImportBatch === batch.batchKey ? 'Deleting...' : 'Delete Batch'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Card.Body>
        </Card>

        <Row>
          {/* Filters */}
          <Col lg={3}>
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Filters</h6>
                  <Button variant="link" size="sm" onClick={clearFilters}>Clear</Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Search Learner</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Name or email..."
                      value={filters.searchTerm}
                      onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small">Category</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.category}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                    >
                      <option value="">All Categories</option>
                      {SALES_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small">Date From</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small">Date To</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={filters.dateTo}
                      onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small">Min Amount</Form.Label>
                    <InputGroup size="sm">
                      <InputGroup.Text>R</InputGroup.Text>
                      <Form.Control
                        type="number"
                        placeholder="0.00"
                        value={filters.minAmount}
                        onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-0">
                    <Form.Label className="small">Max Amount</Form.Label>
                    <InputGroup size="sm">
                      <InputGroup.Text>R</InputGroup.Text>
                      <Form.Control
                        type="number"
                        placeholder="0.00"
                        value={filters.maxAmount}
                        onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
                      />
                    </InputGroup>
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>

            {/* Sales by Category */}
            <Card className="shadow-sm">
              <Card.Header className="bg-white">
                <h6 className="mb-0">Sales by Category</h6>
              </Card.Header>
              <Card.Body>
                {Object.keys(analytics.categoryBreakdown).length === 0 ? (
                  <p className="text-muted small mb-0">No data</p>
                ) : (
                  Object.entries(analytics.categoryBreakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, amount]) => {
                      const percentage = (amount / analytics.totalSales) * 100;
                      return (
                        <div key={category} className="mb-3">
                          <div className="d-flex justify-content-between small mb-1">
                            <span>{category}</span>
                            <strong className="text-success">R {amount.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}</strong>
                          </div>
                          <ProgressBar now={percentage} variant="success" style={{ height: '6px' }} />
                          <small className="text-muted">{percentage.toFixed(1)}%</small>
                        </div>
                      );
                    })
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Main Content */}
          <Col lg={9}>
            {/* Top 10 Learners */}
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-white">
                <h6 className="mb-0">🏆 Top 10 Learners (Filtered)</h6>
              </Card.Header>
              <Card.Body className="p-0">
                {getTopLearners().length === 0 ? (
                  <Alert variant="info" className="m-3 mb-0">No sales data available.</Alert>
                ) : (
                  <Table hover responsive className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-3">Rank</th>
                        <th>Learner</th>
                        <th className="text-center">Transactions</th>
                        <th className="text-end pe-3">Total Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getTopLearners().map((learner, index) => (
                        <tr key={learner.userId}>
                          <td className="ps-3">
                            {index === 0 && <Badge bg="warning" text="dark">🥇</Badge>}
                            {index === 1 && <Badge bg="secondary">🥈</Badge>}
                            {index === 2 && <Badge bg="danger">🥉</Badge>}
                            {index > 2 && <Badge bg="light" text="dark">#{index + 1}</Badge>}
                          </td>
                          <td>
                            <strong>{learner.userName}</strong>
                            <div className="small text-muted">{learner.userEmail}</div>
                          </td>
                          <td className="text-center">
                            <Badge bg="info" pill>{learner.transactionCount}</Badge>
                          </td>
                          <td className="text-end pe-3">
                            <strong className="text-success">
                              R {learner.totalSales.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            {/* Recent Transactions */}
            <Card className="shadow-sm">
              <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Recent Transactions</h6>
                <Badge bg="secondary">{filteredData.length} total</Badge>
              </Card.Header>
              <Card.Body className="p-0">
                {filteredData.length === 0 ? (
                  <Alert variant="info" className="m-3 mb-0">No transactions match your filters.</Alert>
                ) : (
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <Table hover responsive className="mb-0">
                      <thead className="bg-light sticky-top">
                        <tr>
                          <th className="ps-3">Date</th>
                          <th>Learner</th>
                          <th className="text-center">Items</th>
                          <th className="text-end pe-3">Amount</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.slice(0, 50).map(sale => (
                          <tr key={sale.id}>
                            <td className="ps-3 small">
                              {sale.submittedAt?.toLocaleDateString('en-ZA', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td>
                              <strong>{sale.userName}</strong>
                              <div className="small text-muted">{sale.userEmail}</div>
                            </td>
                            <td className="text-center">
                              <Badge bg="secondary" pill>{sale.entryCount}</Badge>
                            </td>
                            <td className="text-end">
                              <strong className="text-success">
                                R {sale.totalAmount?.toFixed(2)}
                              </strong>
                            </td>
                            <td className="pe-3">
                              <details className="small">
                                <summary style={{ cursor: 'pointer' }}>Details</summary>
                                <div className="mt-2">
                                  <strong>Items:</strong>
                                  <ul className="mb-1">
                                    {sale.salesEntries?.map((entry, idx) => (
                                      <li key={idx}>
                                        {entry.category}: {entry.itemName} - R{entry.amount?.toFixed(2)}
                                      </li>
                                    ))}
                                  </ul>
                                  {sale.notes && (
                                    <div>
                                      <strong>Notes:</strong> {sale.notes}
                                    </div>
                                  )}
                                </div>
                              </details>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {filteredData.length > 50 && (
                      <div className="text-center p-3 bg-light small text-muted">
                        Showing first 50 of {filteredData.length} transactions. Use filters to narrow results.
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default FacilitatorSalesDashboard;
